import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Ban, CheckCircle2, ChevronDown, Loader2,
  ShieldAlert, Wallet, Users2, MessageSquare, Crown, Copy,
} from "lucide-react";
import api from "@/lib/api";
import { copyText } from "@/lib/mobile";
import DepositReview from "@/components/DepositReview";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminGamePanel from "@/components/AdminGamePanel";
import AdminFinancePanel from "@/components/AdminFinancePanel";
import AdminEmailSettings from "@/components/AdminEmailSettings";
import AdminGameGuide from "@/components/AdminGameGuide";
import { postSupportMessage } from "@/lib/support";
import SupportComposer from "@/components/SupportComposer";
import SupportImage from "@/components/SupportImage";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const TIER_COLORS = {
  Rookie: "#94A3B8", Bronze: "#CD7F32", Silver: "#CBD5E1",
  Gold: "#F5B301", Platinum: "#7DD3FC", Diamond: "#00E676",
};

function UserCard({ u, onBlock, onUnblock, onReview }) {
  const [expanded, setExpanded] = useState(false);
  const copy = async (t) => {
    try { await copyText(t); toast.success("Copied"); }
    catch { toast.error("Unable to copy address"); }
  };
  return (
    <div
      data-testid={`admin-user-${u.email}`}
      className={`rounded-2xl border bg-[#121721] overflow-hidden ${
        u.blocked ? "border-[#FF3B30]/40" : "border-[#1E293B]"
      }`}
    >
      <button onClick={() => setExpanded((e) => !e)} className="w-full p-4 flex items-center gap-3 text-left">
        <div className="w-9 h-9 rounded-full bg-[#1E2330] flex items-center justify-center text-xs font-bold shrink-0">
          {(u.name || u.email)[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate flex items-center gap-2">
            {u.name || "â€”"}
            {u.role === "admin" && <span className="text-[9px] px-1.5 rounded bg-[#3B82F6]/20 text-[#3B82F6]">ADMIN</span>}
            {u.blocked && <span className="text-[9px] px-1.5 rounded bg-[#FF3B30]/20 text-[#FF3B30]">FROZEN</span>}
          </div>
          <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
        </div>
        <div className="ml-auto text-right shrink-0">
          <div className="font-mono-num text-sm font-semibold text-[#00E676]">{u.balance}</div>
          <div className="flex items-center gap-1 justify-end text-[10px]" style={{ color: TIER_COLORS[u.tier] }}>
            <Crown size={10} /> {u.tier}
          </div>
        </div>
        <ChevronDown size={16} className={`text-slate-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#1E293B]/60 pt-3">
          {u.unfreeze_request && (
            <div className="rounded-lg border border-[#F59E0B]/40 bg-[#1E2330] p-2.5">
              <div className="text-[10px] text-[#F59E0B] font-semibold mb-0.5">Unfreeze request</div>
              <div className="text-xs text-slate-300">"{u.unfreeze_request}"</div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              ["Deposited", u.total_deposited],
              ["Profit", u.earned_profit],
              ["Commission", u.total_commission],
            ].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-[#0B0E14] border border-[#1E293B] p-2">
                <div className="text-[9px] text-slate-500">{l}</div>
                <div className="font-mono-num text-xs">{v}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              ["Direct refs", u.referral_count],
              ["Downline", u.downline_total],
              ["Chain level", u.upline_depth],
            ].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-[#0B0E14] border border-[#1E293B] p-2">
                <div className="text-[9px] text-slate-500">{l}</div>
                <div className="font-mono-num text-xs">{v}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 text-[11px]">
            <div>
              <span className="text-slate-500">Referral code: </span>
              <span className="font-mono-num text-[#00E676]">{u.referral_code}</span>
              {u.referred_by_email && <span className="text-slate-500"> Â· via {u.referred_by_email}</span>}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-slate-500 shrink-0">Deposit addr:</span>
              <span className="font-mono-num text-slate-300 truncate">{u.deposit_address}</span>
              <button onClick={() => copy(u.deposit_address)}><Copy size={12} className="text-slate-500" /></button>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-slate-500 shrink-0">Withdraw addr:</span>
              <span className="font-mono-num text-slate-300 truncate">{u.receiving_address || "â€” not set â€”"}</span>
              {u.receiving_address && <button onClick={() => copy(u.receiving_address)}><Copy size={12} className="text-slate-500" /></button>}
            </div>
          </div>
          {onReview && (
            <Button onClick={() => onReview(u)} className="w-full bg-[#1E2330] text-white">Review USDT deposit</Button>
          )}
          {u.role !== "admin" && (
            u.blocked ? (
              <Button
                data-testid={`unblock-btn-${u.email}`}
                onClick={() => onUnblock(u)}
                className="w-full h-10 bg-[#00E676] hover:bg-[#00C853] text-black font-bold rounded-lg gap-2"
              >
                <CheckCircle2 size={16} /> Unfreeze account
              </Button>
            ) : (
              <Button
                data-testid={`block-btn-${u.email}`}
                onClick={() => onBlock(u)}
                variant="outline"
                className="w-full h-10 border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#3A141A] hover:text-[#FF3B30] bg-transparent rounded-lg gap-2"
              >
                <Ban size={16} /> Freeze account
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
}

function ChatThread({ userId, onBack }) {
  const [data, setData] = useState(null);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/chats/${userId}/messages`);
    setData(data);
  }, [userId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  const send = async (text, file) => {
    const { data: result } = await postSupportMessage(`/admin/chats/${userId}/messages`, text, file);
    setData((prev) => ({ ...prev, messages: [...(prev?.messages || []), result.message] }));
  };

  return (
    <div className="flex flex-col h-[70vh] rounded-2xl border border-[#1E293B] bg-[#121721] overflow-hidden">
      <div className="flex items-center gap-2 px-3 h-12 border-b border-[#1E293B]">
        <button onClick={onBack} data-testid="chat-thread-back"><ArrowLeft size={18} /></button>
        <div className="text-sm font-semibold truncate">{data?.user?.name || data?.user?.email || "User"}</div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {data?.messages?.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm ${
              m.sender === "admin" ? "bg-[#3B82F6] text-white rounded-br-sm" : "bg-[#1E2330] text-slate-100 rounded-bl-sm"
            }`}>
              {m.image && <SupportImage image={m.image} />}
              {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <SupportComposer onSend={send} placeholder="Reply…" inputTestId="admin-chat-input" sendTestId="admin-chat-send" />
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockMsg, setBlockMsg] = useState("");
  const [reviewTarget, setReviewTarget] = useState(null);
  const [depositMode, setDepositMode] = useState("");
  const [selectedThread, setSelectedThread] = useState(null);

  const loadUsers = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data.users);
    setDepositMode(data.deposit_processing_mode || "automatic");
    setLoading(false);
  }, []);
  const loadThreads = useCallback(async () => {
    const { data } = await api.get("/admin/chats");
    setThreads(data.threads);
  }, []);

  useEffect(() => {
    loadUsers();
    loadThreads();
    const id = setInterval(loadThreads, 5000);
    return () => clearInterval(id);
  }, [loadUsers, loadThreads]);

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0B0E14] text-slate-400 gap-3">
        <ShieldAlert className="text-[#FF3B30]" size={32} />
        Admin access only.
        <Button onClick={() => navigate("/")} className="bg-[#1E2330]">Go home</Button>
      </div>
    );
  }

  const confirmBlock = async () => {
    if (!blockMsg.trim()) return;
    try {
      await api.post(`/admin/users/${blockTarget.id}/block`, { message: blockMsg.trim() });
      toast.success(`${blockTarget.email} frozen`);
      setBlockTarget(null);
      setBlockMsg("");
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const doUnblock = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unblock`);
      toast.success(`${u.email} unfrozen`);
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filtered = users.filter(
    (u) => u.email.toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase())
  );
  const totalUnread = threads.reduce((s, t) => s + t.unread, 0);

  return (
    <div className="pb-10 grain min-h-screen bg-[#0B0E14]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B0E14]/80 border-b border-[#1E293B]">
        <div className="max-w-md mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="admin-back-btn" onClick={() => navigate("/")} className="w-9 h-9 rounded-full bg-[#121721] border border-[#1E293B] flex items-center justify-center">
            <ArrowLeft size={18} />
          </button>
          <span className="font-display font-bold text-lg flex items-center gap-2">
            <ShieldAlert size={18} className="text-[#3B82F6]" /> Admin Panel
          </span>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 pt-4">
        <Tabs defaultValue="users">
          <TabsList className="w-full h-auto grid grid-cols-3 gap-1 bg-[#121721] border border-[#1E293B] [&>button]:px-1 [&>button]:text-xs">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="flex-1 data-[state=active]:bg-[#1E2330] gap-1.5">
              Users
            </TabsTrigger>
            <TabsTrigger value="chats" data-testid="admin-tab-chats" className="flex-1 data-[state=active]:bg-[#1E2330] gap-1.5">
              Support {totalUnread > 0 && <span className="ml-1 min-w-4 h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[9px] flex items-center justify-center">{totalUnread}</span>}
            </TabsTrigger>
            <TabsTrigger value="game" className="w-full data-[state=active]:bg-[#1E2330]">Outcomes</TabsTrigger>
            <TabsTrigger value="finance" className="w-full data-[state=active]:bg-[#1E2330]">Finance</TabsTrigger>
            <TabsTrigger value="email" className="w-full data-[state=active]:bg-[#1E2330]">Email</TabsTrigger>
            <TabsTrigger value="guide" className="w-full data-[state=active]:bg-[#1E2330]">How to Play</TabsTrigger>
          </TabsList>
          <TabsContent value="game" className="mt-4"><AdminGamePanel /></TabsContent>
          <TabsContent value="finance" className="mt-4"><AdminFinancePanel /></TabsContent>
          <TabsContent value="email" className="mt-4"><AdminEmailSettings /></TabsContent>
          <TabsContent value="guide" className="mt-4"><AdminGameGuide /></TabsContent>

          <TabsContent value="users" className="mt-4 space-y-3">
            {depositMode === "manual" && <p className="text-xs text-amber-300">Manual deposits enabled. Expand a user to review and approve a transfer.</p>}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="admin-user-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search users by name or email"
                className="h-11 pl-9 bg-[#121721] border-[#1E293B] text-white rounded-xl"
              />
            </div>
            {loading ? (
              <div className="flex justify-center pt-10"><Loader2 className="animate-spin text-[#00E676]" /></div>
            ) : (
              filtered.map((u) => (
                <UserCard key={u.id} u={u} onBlock={(x) => setBlockTarget(x)} onUnblock={doUnblock} onReview={depositMode === "manual" ? setReviewTarget : null} />
              ))
            )}
          </TabsContent>

          <TabsContent value="chats" className="mt-4">
            {selectedThread ? (
              <ChatThread userId={selectedThread} onBack={() => { setSelectedThread(null); loadThreads(); }} />
            ) : threads.length === 0 ? (
              <div className="text-center text-sm text-slate-500 pt-10">No support conversations yet.</div>
            ) : (
              <div className="space-y-2">
                {threads.map((t) => (
                  <button
                    key={t.user_id}
                    data-testid={`admin-thread-${t.email}`}
                    onClick={() => setSelectedThread(t.user_id)}
                    className="w-full rounded-xl border border-[#1E293B] bg-[#121721] p-3 flex items-center gap-3 text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#1E2330] flex items-center justify-center text-xs font-bold shrink-0">
                      {(t.name || t.email)[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{t.name || t.email}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {t.last_sender === "admin" ? "You: " : ""}{t.last_text}
                      </div>
                    </div>
                    {t.unread > 0 && (
                      <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                        {t.unread}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {reviewTarget && <DepositReview target={reviewTarget} onClose={() => setReviewTarget(null)} onApproved={loadUsers} />}
      <Dialog open={!!blockTarget} onOpenChange={(o) => !o && setBlockTarget(null)}>
        <DialogContent className="bg-[#121721] border-[#1E293B] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Freeze {blockTarget?.name || blockTarget?.email}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400">
            The user will be locked out and shown this message when they log in.
          </p>
          <Textarea
            data-testid="block-message-input"
            value={blockMsg}
            onChange={(e) => setBlockMsg(e.target.value)}
            placeholder="Reason / message to show the userâ€¦"
            className="bg-[#0B0E14] border-[#1E293B] text-white min-h-24"
          />
          <Button
            data-testid="confirm-block-btn"
            onClick={confirmBlock}
            className="w-full h-11 bg-[#FF3B30] hover:bg-[#D50000] text-white font-bold rounded-xl"
          >
            Freeze Account
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
